// 사이트탐색알림(중고거래) 감시조건 관리 화면 (tb_marketplace_watch)
// 좌측: 감시조건 목록 그리드 / 우측: 등록·수정 폼 (브랜드/모델/제외 키워드는 칩 입력)
import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchWatches, createWatch, updateWatch, deleteWatch,
  fetchMarketplaceSettings, updateMarketplaceSettings,
} from '../../api/marketplace';
import PageLayout from '../../components/PageLayout';
import './WatchConditionPage.css';

const SITE_OPTIONS = [
  { value: 'WASSADA',   label: '와싸다' },
  { value: 'SORIAUDIO', label: '소리전자' },
];
const SITE_LABEL = Object.fromEntries(SITE_OPTIONS.map((s) => [s.value, s.label]));

const EMPTY_FORM = {
  site_code: 'WASSADA',
  brand_keywords: '',
  model_keywords: '',
  exclude_keywords: '',
  min_price: '',
  max_price: '',
  region_filter: '',
  is_active: true,
};

// 브랜드/모델/제외 키워드 칩 입력 - 내부적으로 콤마구분 문자열(csv)로 값을 주고받음
const ChipInput = ({ csv, onChange, placeholder }) => {
  const [draft, setDraft] = useState('');
  const chips = csv ? csv.split(',').map((s) => s.trim()).filter(Boolean) : [];

  const addChip = () => {
    const v = draft.trim();
    if (!v) return;
    if (!chips.includes(v)) onChange([...chips, v].join(','));
    setDraft('');
  };

  const removeChip = (chip) => {
    onChange(chips.filter((c) => c !== chip).join(','));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addChip();
    } else if (e.key === 'Backspace' && !draft && chips.length > 0) {
      removeChip(chips[chips.length - 1]);
    }
  };

  return (
    <div className="chip-input-box">
      {chips.map((chip) => (
        <span key={chip} className="chip">
          {chip}
          <button type="button" className="chip-remove" onClick={() => removeChip(chip)}>×</button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={addChip}
        placeholder={chips.length === 0 ? placeholder : ''}
      />
    </div>
  );
};

const WatchConditionPage = () => {
  const [watches,   setWatches]   = useState([]);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [message,   setMessage]   = useState({ text: '', type: '' });

  // 조용한 시간(발송 금지 시간대) 설정
  const [quietEnabled, setQuietEnabled] = useState(false);
  const [quietStart,   setQuietStart]   = useState('23:00');
  const [quietEnd,     setQuietEnd]     = useState('08:00');
  const [quietSaving,  setQuietSaving]  = useState(false);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const loadWatches = useCallback(async () => {
    try {
      setWatches(await fetchWatches());
    } catch {
      showMessage('목록 조회에 실패했습니다.', 'error');
    }
  }, []);

  const loadQuietSettings = useCallback(async () => {
    try {
      const res = await fetchMarketplaceSettings();
      if (res.quiet_start && res.quiet_end) {
        setQuietEnabled(true);
        setQuietStart(res.quiet_start);
        setQuietEnd(res.quiet_end);
      } else {
        setQuietEnabled(false);
      }
    } catch { /* 조회 실패 시 기본값 유지 */ }
  }, []);

  useEffect(() => { loadWatches(); loadQuietSettings(); }, [loadWatches, loadQuietSettings]);

  const handleQuietSave = async () => {
    setQuietSaving(true);
    try {
      await updateMarketplaceSettings({
        quiet_start: quietEnabled ? quietStart : null,
        quiet_end:   quietEnabled ? quietEnd   : null,
      });
      showMessage('조용한 시간 설정이 저장되었습니다.');
    } catch (err) {
      showMessage(err.response?.data?.detail || '설정 저장에 실패했습니다.', 'error');
    } finally {
      setQuietSaving(false);
    }
  };

  const handleRowClick = (w) => {
    setEditingId(w.id);
    setForm({
      site_code:        w.site_code,
      brand_keywords:   w.brand_keywords || '',
      model_keywords:   w.model_keywords || '',
      exclude_keywords: w.exclude_keywords || '',
      min_price:        w.min_price ?? '',
      max_price:        w.max_price ?? '',
      region_filter:    w.region_filter || '',
      is_active:        w.is_active,
    });
  };

  const handleNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleFieldChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.brand_keywords.trim() && !form.model_keywords.trim()) {
      showMessage('브랜드 또는 모델 키워드를 1개 이상 입력해주세요.', 'error');
      return;
    }
    const payload = {
      ...form,
      min_price: form.min_price === '' ? null : Number(form.min_price),
      max_price: form.max_price === '' ? null : Number(form.max_price),
      region_filter: form.region_filter.trim() || null,
      exclude_keywords: form.exclude_keywords.trim() || null,
    };
    setLoading(true);
    try {
      if (editingId === null) {
        await createWatch(payload);
        showMessage('감시조건이 등록되었습니다.');
      } else {
        await updateWatch(editingId, payload);
        showMessage('감시조건이 수정되었습니다.');
      }
      await loadWatches();
      handleNew();
    } catch (err) {
      showMessage(err.response?.data?.detail || '저장에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('이 감시조건을 삭제하시겠습니까?')) return;
    setLoading(true);
    try {
      await deleteWatch(id);
      showMessage('감시조건이 삭제되었습니다.');
      await loadWatches();
      if (editingId === id) handleNew();
    } catch (err) {
      showMessage(err.response?.data?.detail || '삭제에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toolbar = (
    <button className="btn btn-primary" onClick={handleNew}>+ 신규</button>
  );

  return (
    <PageLayout title="감시조건관리" toolbar={toolbar} message={message}>
      <div className="quiet-hours-box">
        <label className="quiet-hours-toggle">
          <input
            type="checkbox"
            checked={quietEnabled}
            onChange={(e) => setQuietEnabled(e.target.checked)}
          />
          {' '}조용한 시간 사용 (이 시간대엔 이력만 쌓이고, 종료 후 밀린 메시지를 한꺼번에 발송)
        </label>
        <input
          type="time"
          value={quietStart}
          onChange={(e) => setQuietStart(e.target.value)}
          disabled={!quietEnabled}
        />
        <span>~</span>
        <input
          type="time"
          value={quietEnd}
          onChange={(e) => setQuietEnd(e.target.value)}
          disabled={!quietEnabled}
        />
        <button className="btn btn-secondary btn-sm" onClick={handleQuietSave} disabled={quietSaving}>
          {quietSaving ? '저장 중...' : '저장'}
        </button>
      </div>

      <div className="watch-body">

        {/* ── 좌측: 감시조건 목록 그리드 ── */}
        <div className="watch-list-panel">
          <div className="grid-wrapper">
            <table className="grid-table">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>사이트</th>
                  <th>브랜드</th>
                  <th>모델</th>
                  <th className="col-center" style={{ width: 60 }}>사용</th>
                  <th className="col-center" style={{ width: 60 }}>삭제</th>
                </tr>
              </thead>
              <tbody>
                {watches.length === 0 ? (
                  <tr><td colSpan={5} className="grid-empty">등록된 감시조건이 없습니다.</td></tr>
                ) : (
                  watches.map((w) => (
                    <tr
                      key={w.id}
                      onClick={() => handleRowClick(w)}
                      className={editingId === w.id ? 'selected' : ''}
                    >
                      <td>{SITE_LABEL[w.site_code] || w.site_code}</td>
                      <td className="col-desc">{w.brand_keywords}</td>
                      <td className="col-desc">{w.model_keywords}</td>
                      <td className="col-center">{w.is_active ? 'ON' : 'OFF'}</td>
                      <td className="col-center">
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={(e) => handleDelete(w.id, e)}
                          disabled={loading}
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── 우측: 등록·수정 폼 ── */}
        <div className="watch-form-panel">
          <h3 className="form-title">{editingId === null ? '신규 등록' : '감시조건 수정'}</h3>

          <div className="form-group">
            <label>사이트 <span className="required">*</span></label>
            <select
              value={form.site_code}
              onChange={(e) => handleFieldChange('site_code', e.target.value)}
            >
              {SITE_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>브랜드 키워드 (OR매칭)</label>
            <ChipInput
              csv={form.brand_keywords}
              onChange={(v) => handleFieldChange('brand_keywords', v)}
              placeholder="예) proac, 프로악 (Enter로 추가)"
            />
          </div>

          <div className="form-group">
            <label>모델 키워드 (OR매칭)</label>
            <ChipInput
              csv={form.model_keywords}
              onChange={(v) => handleFieldChange('model_keywords', v)}
              placeholder="예) d2, d-two, 디투 (Enter로 추가)"
            />
          </div>

          <div className="form-group">
            <label>제외 키워드</label>
            <ChipInput
              csv={form.exclude_keywords}
              onChange={(v) => handleFieldChange('exclude_keywords', v)}
              placeholder="예) 삽니다, 구합니다, 견적, 파츠"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>최소 가격</label>
              <input
                type="number"
                value={form.min_price}
                onChange={(e) => handleFieldChange('min_price', e.target.value)}
                placeholder="제한없음"
                min={0}
              />
            </div>
            <div className="form-group">
              <label>최대 가격</label>
              <input
                type="number"
                value={form.max_price}
                onChange={(e) => handleFieldChange('max_price', e.target.value)}
                placeholder="제한없음"
                min={0}
              />
            </div>
          </div>

          <div className="form-group">
            <label>지역 필터</label>
            <input
              type="text"
              value={form.region_filter}
              onChange={(e) => handleFieldChange('region_filter', e.target.value)}
              placeholder="예) 서울 (텍스트 포함여부로 판단)"
              maxLength={200}
            />
          </div>

          <div className="form-group form-group-checkbox">
            <label>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => handleFieldChange('is_active', e.target.checked)}
              />
              {' '}사용
            </label>
          </div>

          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
              {loading ? '저장 중...' : '저장'}
            </button>
            <button className="btn btn-secondary" onClick={handleNew} disabled={loading}>취소</button>
          </div>
        </div>

      </div>
    </PageLayout>
  );
};

export default WatchConditionPage;
